import React from 'react';
import { useLocalization } from '../../../../../config/localization';
import Icon, { ICONS } from '../../../../ui/icons';
import FileUpload from '../../../../ui/FileUpload';
import type { TileConfigSectionProps } from './index';

export default function TileConfigSection({
  formData,
  onFormDataChange,
  onTileMediaUpload,
  language
}: TileConfigSectionProps) {
  const t = useLocalization(language);

  const handleDisplayTypeChange = (displayType: string | null) => {
    const newConfig = {
      ...formData.tile_config,
      displayType: displayType || null,
      imageUrl: null,
      imageUrl2: null,
      videoUrl: null,
      slideshowImages: displayType === 'slideshow' ? [] : (formData.tile_config?.slideshowImages || []),
      slideshowDelay: displayType === 'slideshow' ? 1000 : (formData.tile_config?.slideshowDelay || 1000),
      slideshowTransition: displayType === 'slideshow' ? 'fade' : (formData.tile_config?.slideshowTransition || 'fade')
    };
    onFormDataChange('tile_config', newConfig);
  };

  const handleMediaDelete = (mediaType: 'imageUrl' | 'imageUrl2' | 'videoUrl') => {
    const newConfig = { ...formData.tile_config, [mediaType]: null };
    onFormDataChange('tile_config', newConfig);
  };

  const handleSlideshowImageDelete = (index: number) => {
    const updatedImages = [...(formData.tile_config?.slideshowImages || [])];
    updatedImages[index] = '';
    const newConfig = {
      ...formData.tile_config,
      slideshowImages: updatedImages.filter(url => url !== '')
    };
    onFormDataChange('tile_config', newConfig);
  };

  const displayTypeOptions = [
    { value: '', label: t.adminWorkflows?.tileDisplay?.none || 'No Tile', icon: ICONS.X, color: 'text-gray-400' },
    { value: 'single_image', label: t.adminWorkflows?.tileDisplay?.singleImage || 'Single Image', icon: ICONS.IMAGE, color: 'text-blue-400' },
    { value: 'image_pair', label: t.adminWorkflows?.tileDisplay?.imagePair || 'Image Pair (before/after)', icon: ICONS.GALLERY, color: 'text-purple-400' },
    { value: 'single_video', label: t.adminWorkflows?.tileDisplay?.singleVideo || 'Video', icon: ICONS.VIDEO, color: 'text-green-400' },
    { value: 'slideshow', label: t.adminWorkflows?.tileDisplay?.slideshow || 'Slideshow (up to 5 images)', icon: ICONS.GALLERY, color: 'text-orange-400' }
  ];

  const currentDisplayType = formData.tile_config?.displayType || '';

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <section>
        <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <Icon name={ICONS.IMAGE} size={20} className="text-purple-400" />
          {t.adminWorkflows?.tileDisplay?.title || 'Tile Configuration'}
        </h3>
        <p className="text-sm text-gray-400 mb-4">
          {t.admin?.tileConfigDesc || 'Configure how this workflow appears in the workflow gallery.'}
        </p>
      </section>

      {/* Display Type Selector */}
      <section className="bg-gray-800/30 rounded-xl border border-gray-700/50 p-4">
        <label className="block text-sm font-medium text-gray-300 mb-3">
          {t.admin?.displayType || 'Display Type'}
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {displayTypeOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleDisplayTypeChange(option.value || null)}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                currentDisplayType === option.value
                  ? 'bg-purple-900/30 border-purple-500 text-white'
                  : 'bg-gray-800/50 border-gray-600/50 text-gray-300 hover:border-gray-500 hover:bg-gray-800'
              }`}
            >
              <div className="flex items-center gap-3">
                <Icon name={option.icon} size={24} className={option.color} />
                <span className="font-medium">{option.label}</span>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Media Upload based on Display Type */}
      {currentDisplayType && (
        <section className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 border border-purple-500/30 rounded-xl p-4">
          {/* Single Image Upload */}
          {currentDisplayType === 'single_image' && (
            <div>
              <h4 className="text-sm font-medium text-purple-300 mb-3 flex items-center gap-2">
                <Icon name={ICONS.IMAGE} size={16} />
                {t.adminWorkflows?.tileDisplay?.uploadImage || 'Upload Image'}
              </h4>
              <FileUpload
                label=""
                currentUrl={formData.tile_config?.imageUrl}
                onUpload={(file) => onTileMediaUpload(file, 'image')}
                onDelete={() => handleMediaDelete('imageUrl')}
                accept="image/*"
                maxSize={5 * 1024 * 1024}
                uploadingText={t.adminWorkflows?.tileDisplay?.uploading || 'Uploading...'}
                deleteText={t.adminWorkflows?.tileDisplay?.deleteMedia || 'Delete'}
                replaceText={t.adminWorkflows?.tileDisplay?.replaceMedia || 'Replace'}
                confirmDeleteText={t.adminWorkflows?.tileDisplay?.confirmDeleteMedia || 'Delete this media?'}
                fileUploadedText={t.adminWorkflows?.tileDisplay?.fileUploaded || 'File uploaded'}
                dropFileHereText={t.adminWorkflows?.tileDisplay?.dropFileHere || 'Drop file here...'}
                dragDropText={t.adminWorkflows?.tileDisplay?.dragDrop || 'Drag file here or click to select'}
                uploadErrorText={t.adminWorkflows?.tileDisplay?.uploadError || 'File upload error'}
              />
            </div>
          )}

          {/* Image Pair Upload */}
          {currentDisplayType === 'image_pair' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-blue-300 mb-3 flex items-center gap-2">
                    <Icon name={ICONS.IMAGE} size={16} />
                    {t.adminWorkflows?.tileDisplay?.uploadBefore || 'Upload "Before" Image'}
                  </h4>
                  <FileUpload
                    label=""
                    currentUrl={formData.tile_config?.imageUrl}
                    onUpload={(file) => onTileMediaUpload(file, 'image')}
                    onDelete={() => handleMediaDelete('imageUrl')}
                    accept="image/*"
                    maxSize={5 * 1024 * 1024}
                    uploadingText={t.adminWorkflows?.tileDisplay?.uploading || 'Uploading...'}
                    deleteText={t.adminWorkflows?.tileDisplay?.deleteMedia || 'Delete'}
                    replaceText={t.adminWorkflows?.tileDisplay?.replaceMedia || 'Replace'}
                    confirmDeleteText={t.adminWorkflows?.tileDisplay?.confirmDeleteMedia || 'Delete this media?'}
                    fileUploadedText={t.adminWorkflows?.tileDisplay?.fileUploaded || 'File uploaded'}
                    dropFileHereText={t.adminWorkflows?.tileDisplay?.dropFileHere || 'Drop file here...'}
                    dragDropText={t.adminWorkflows?.tileDisplay?.dragDrop || 'Drag file here or click to select'}
                    uploadErrorText={t.adminWorkflows?.tileDisplay?.uploadError || 'File upload error'}
                  />
                </div>

                <div>
                  <h4 className="text-sm font-medium text-green-300 mb-3 flex items-center gap-2">
                    <Icon name={ICONS.IMAGE} size={16} />
                    {t.adminWorkflows?.tileDisplay?.uploadAfter || 'Upload "After" Image'}
                  </h4>
                  <FileUpload
                    label=""
                    currentUrl={formData.tile_config?.imageUrl2}
                    onUpload={(file) => onTileMediaUpload(file, 'image2')}
                    onDelete={() => handleMediaDelete('imageUrl2')}
                    accept="image/*"
                    maxSize={5 * 1024 * 1024}
                    uploadingText={t.adminWorkflows?.tileDisplay?.uploading || 'Uploading...'}
                    deleteText={t.adminWorkflows?.tileDisplay?.deleteMedia || 'Delete'}
                    replaceText={t.adminWorkflows?.tileDisplay?.replaceMedia || 'Replace'}
                    confirmDeleteText={t.adminWorkflows?.tileDisplay?.confirmDeleteMedia || 'Delete this media?'}
                    fileUploadedText={t.adminWorkflows?.tileDisplay?.fileUploaded || 'File uploaded'}
                    dropFileHereText={t.adminWorkflows?.tileDisplay?.dropFileHere || 'Drop file here...'}
                    dragDropText={t.adminWorkflows?.tileDisplay?.dragDrop || 'Drag file here or click to select'}
                    uploadErrorText={t.adminWorkflows?.tileDisplay?.uploadError || 'File upload error'}
                  />
                </div>
              </div>

              {/* Comparison Mode */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t.adminWorkflows?.tileDisplay?.comparisonMode || 'Comparison Mode'}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      const newConfig = { ...formData.tile_config, comparisonMode: 'slider' as const };
                      onFormDataChange('tile_config', newConfig);
                    }}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      formData.tile_config?.comparisonMode === 'slider' || !formData.tile_config?.comparisonMode
                        ? 'bg-blue-900/30 border-blue-500 text-white'
                        : 'bg-gray-800/50 border-gray-600/50 text-gray-300 hover:border-gray-500'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon name={ICONS.SETTINGS} size={16} className="text-blue-400" />
                      <span>{t.adminWorkflows?.tileDisplay?.slider || 'Slider'}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {t.admin?.sliderDesc || 'Interactive slider to compare images'}
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      const newConfig = { ...formData.tile_config, comparisonMode: 'auto_switch' as const };
                      onFormDataChange('tile_config', newConfig);
                    }}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      formData.tile_config?.comparisonMode === 'auto_switch'
                        ? 'bg-purple-900/30 border-purple-500 text-white'
                        : 'bg-gray-800/50 border-gray-600/50 text-gray-300 hover:border-gray-500'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon name={ICONS.PLAY} size={16} className="text-purple-400" />
                      <span>{t.adminWorkflows?.tileDisplay?.autoSwitch || 'Auto-Switch'}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {t.admin?.autoSwitchDesc || 'Automatically switches between images'}
                    </p>
                  </button>
                </div>
              </div>

              {/* Auto-switch Delay */}
              {formData.tile_config?.comparisonMode === 'auto_switch' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t.adminWorkflows?.tileDisplay?.delayMs || 'Delay (ms)'}
                  </label>
                  <input
                    type="number"
                    min="500"
                    max="10000"
                    step="100"
                    value={formData.tile_config?.autoSwitchDelay || 2000}
                    onChange={(e) => {
                      const newConfig = {
                        ...formData.tile_config,
                        autoSwitchDelay: parseInt(e.target.value) || 2000
                      };
                      onFormDataChange('tile_config', newConfig);
                    }}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t.admin?.autoSwitchDelayHint || 'Time between image switches (500-10000ms)'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Single Video Upload */}
          {currentDisplayType === 'single_video' && (
            <div>
              <h4 className="text-sm font-medium text-green-300 mb-3 flex items-center gap-2">
                <Icon name={ICONS.VIDEO} size={16} />
                {t.adminWorkflows?.tileDisplay?.uploadVideo || 'Upload Video'}
              </h4>
              <FileUpload
                label=""
                currentUrl={formData.tile_config?.videoUrl}
                onUpload={(file) => onTileMediaUpload(file, 'video')}
                onDelete={() => handleMediaDelete('videoUrl')}
                accept="video/*"
                maxSize={20 * 1024 * 1024}
                uploadingText={t.adminWorkflows?.tileDisplay?.uploading || 'Uploading...'}
                deleteText={t.adminWorkflows?.tileDisplay?.deleteMedia || 'Delete'}
                replaceText={t.adminWorkflows?.tileDisplay?.replaceMedia || 'Replace'}
                confirmDeleteText={t.adminWorkflows?.tileDisplay?.confirmDeleteMedia || 'Delete this media?'}
                fileUploadedText={t.adminWorkflows?.tileDisplay?.fileUploaded || 'File uploaded'}
                dropFileHereText={t.adminWorkflows?.tileDisplay?.dropFileHere || 'Drop file here...'}
                dragDropText={t.adminWorkflows?.tileDisplay?.dragDrop || 'Drag file here or click to select'}
                uploadErrorText={t.adminWorkflows?.tileDisplay?.uploadError || 'File upload error'}
              />
              <p className="text-xs text-gray-500 mt-2">
                {t.admin?.videoUploadHint || 'Max file size: 20MB. Supported formats: MP4, WebM'}
              </p>
            </div>
          )}

          {/* Slideshow Upload */}
          {currentDisplayType === 'slideshow' && (
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-orange-300 mb-3 flex items-center gap-2">
                <Icon name={ICONS.GALLERY} size={16} />
                {t.adminWorkflows?.tileDisplay?.slideshowImages || 'Slideshow Images'}
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[0, 1, 2, 3, 4].map((index) => {
                  const images = formData.tile_config?.slideshowImages || [];
                  const currentUrl = images[index] || null;

                  return (
                    <FileUpload
                      key={index}
                      label={`${t.adminWorkflows?.tileDisplay?.slideshowImage || 'Image'} ${index + 1}${index === 0 ? ' (required)' : ''}`}
                      currentUrl={currentUrl}
                      onUpload={(file) => onTileMediaUpload(file, `slideshow_${index}`)}
                      onDelete={() => handleSlideshowImageDelete(index)}
                      accept="image/*"
                      maxSize={5 * 1024 * 1024}
                      uploadingText={t.adminWorkflows?.tileDisplay?.uploading || 'Uploading...'}
                      deleteText={t.adminWorkflows?.tileDisplay?.deleteMedia || 'Delete'}
                      replaceText={t.adminWorkflows?.tileDisplay?.replaceMedia || 'Replace'}
                      confirmDeleteText={t.adminWorkflows?.tileDisplay?.confirmDeleteMedia || 'Delete this media?'}
                      fileUploadedText={t.adminWorkflows?.tileDisplay?.fileUploaded || 'File uploaded'}
                      dropFileHereText={t.adminWorkflows?.tileDisplay?.dropFileHere || 'Drop file here...'}
                      dragDropText={t.adminWorkflows?.tileDisplay?.dragDrop || 'Drag file here or click to select'}
                      uploadErrorText={t.adminWorkflows?.tileDisplay?.uploadError || 'File upload error'}
                    />
                  );
                })}
              </div>

              {/* Transition Type */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t.adminWorkflows?.tileDisplay?.transitionType || 'Transition Effect'}
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'instant', label: t.adminWorkflows?.tileDisplay?.transitionInstant || 'Instant', icon: ICONS.ZAPS },
                    { value: 'fade', label: t.adminWorkflows?.tileDisplay?.transitionFade || 'Fade', icon: ICONS.GALLERY },
                    { value: 'slide', label: t.adminWorkflows?.tileDisplay?.transitionSlide || 'Slide', icon: ICONS.CHEVRON_LEFT }
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        const newConfig = {
                          ...formData.tile_config,
                          slideshowTransition: option.value as 'instant' | 'fade' | 'slide'
                        };
                        onFormDataChange('tile_config', newConfig);
                      }}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        formData.tile_config?.slideshowTransition === option.value
                          ? 'bg-orange-900/30 border-orange-500 text-white'
                          : 'bg-gray-800/50 border-gray-600/50 text-gray-300 hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Icon name={option.icon} size={16} className="text-orange-400" />
                        <span className="text-sm">{option.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Slideshow Delay */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t.adminWorkflows?.tileDisplay?.slideshowDelay || 'Slide Duration (ms)'}
                </label>
                <input
                  type="number"
                  min="200"
                  max="5000"
                  step="100"
                  value={formData.tile_config?.slideshowDelay || 1000}
                  onChange={(e) => {
                    const newConfig = {
                      ...formData.tile_config,
                      slideshowDelay: parseInt(e.target.value) || 1000
                    };
                    onFormDataChange('tile_config', newConfig);
                  }}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t.adminWorkflows?.tileDisplay?.slideshowDelayHint || 'Time each slide is displayed (200-5000ms)'}
                </p>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Preview Info */}
      {!currentDisplayType && (
        <section className="bg-gray-800/30 rounded-xl border border-gray-700/50 p-6 text-center">
          <Icon name={ICONS.IMAGE} size={48} className="mx-auto mb-4 text-gray-600" />
          <p className="text-gray-400 mb-2">
            {t.admin?.noTileConfigured || 'No tile configured'}
          </p>
          <p className="text-sm text-gray-500">
            {t.admin?.noTileConfiguredDesc || 'Select a display type above to configure how this workflow appears in the gallery.'}
          </p>
        </section>
      )}
    </div>
  );
}
